import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formsApi } from '../api/api';
import { Knowledge } from '../types/form';
import { toast } from 'sonner';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useErrorState } from '../hooks/useErrorState';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

const SharedKnowledge: React.FC = () => {
  const [knowledge, setKnowledge] = useState<Knowledge[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { handleApiError } = useErrorHandler();
  const { setError: setPageError } = useErrorState();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState<Knowledge | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    options: {} as { [key: string]: string }
  });
  const [newOptionValue, setNewOptionValue] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchKnowledge = useCallback(async () => {
    try {
      const response = await formsApi.getKnowledge();
      
      let knowledgeData = response.data;
      if (knowledgeData && typeof knowledgeData === 'object' && 'results' in knowledgeData) {
        knowledgeData = (knowledgeData as any).results;
      }
      setKnowledge(Array.isArray(knowledgeData) ? knowledgeData : []);
    } catch (error: any) {
      setKnowledge([]);
      setPageError(handleApiError(error as any, 'load knowledge'));
    } finally {
      setLoading(false);
    }
  }, [handleApiError, setPageError]);

  useEffect(() => {
    fetchKnowledge();
  }, [fetchKnowledge]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      options: {}
    });
    setNewOptionValue('');
  };

  const handleCreate = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (item: Knowledge) => {
    setEditingKnowledge(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      options: { ...item.options }
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      await formsApi.deleteKnowledge(deleteId);
      setKnowledge(knowledge.filter(item => item.id !== deleteId));
      toast.success('Knowledge deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete knowledge');
    }
  };

  const addOption = React.useCallback(() => {
    if (newOptionValue.trim()) {
      const key = `option${Object.keys(formData.options).length + 1}`;
      setFormData(prev => ({
        ...prev,
        options: {
          ...prev.options,
          [key]: newOptionValue.trim()
        }
      }));
      setNewOptionValue('');
    }
  }, [newOptionValue, formData.options]);

  const removeOption = React.useCallback((keyToRemove: string) => {
    setFormData(prev => {
      const newOptions = { ...prev.options };
      delete newOptions[keyToRemove];
      
      const reindexedOptions = Object.values(newOptions).reduce((acc, value, index) => {
        acc[`option${index + 1}`] = value;
        return acc;
      }, {} as { [key: string]: string });
      
      return {
        ...prev,
        options: reindexedOptions
      };
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (Object.keys(formData.options).length === 0) {
      toast.error('At least one option is required');
      return;
    }

    try {
      if (editingKnowledge) {
        const response = await formsApi.updateKnowledge(editingKnowledge.id, formData);
        setKnowledge(knowledge.map(item => 
          item.id === editingKnowledge.id ? response.data : item
        ));
        setIsEditDialogOpen(false);
        setEditingKnowledge(null);
        toast.success('Knowledge updated successfully');
      } else {
        const response = await formsApi.createKnowledge(formData);
        setKnowledge(prev => [...prev, response.data]);
        setIsCreateDialogOpen(false);
        toast.success('Knowledge created successfully');
      }
      resetForm();
      
      await fetchKnowledge();
    } catch (error: any) {
      toast.error(`Failed to ${editingKnowledge ? 'update' : 'create'} knowledge`, {
        description: error.response?.data?.detail || error.message || 'Please try again'
      });
    }
  };

  const renderOptionsEditor = () => (
    <div className="space-y-4">
      <Label>Options</Label>
      <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
        {Object.entries(formData.options).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2 p-2 border rounded">
            <div className="flex-1">
              {value}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeOption(key)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="Enter option value"
          value={newOptionValue}
          onChange={(e) => setNewOptionValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOption();
            }
          }}
        />
        <Button 
          type="button"
          onClick={addOption}
          disabled={!newOptionValue.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading knowledge...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Shared Knowledge</h1>
          <p className="text-muted-foreground">
            Create and manage reusable options for form fields
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create New
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {knowledge.map((item) => (
          <Card key={item.id}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="truncate">{item.name}</span>
                  {item.shared && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      Shared
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(item)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardTitle>
              {item.description && (
                <CardDescription className="mt-1">
                  {item.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {Object.values(item.options).map((value, index) => (
                  <div key={index} className="text-sm">
                    {value}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog 
        open={isCreateDialogOpen || isEditDialogOpen} 
        onOpenChange={(open) => {
          if (!open) {
            resetForm();
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            setEditingKnowledge(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingKnowledge ? 'Edit Knowledge' : 'Create New Knowledge'}
            </DialogTitle>
            <DialogDescription>
              Add shared knowledge that can be used across multiple forms
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter a name for this knowledge"
                />
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter a description"
                />
              </div>

              {renderOptionsEditor()}
            </div>

            <DialogFooter>
              <Button type="submit">
                {editingKnowledge ? 'Save Changes' : 'Create Knowledge'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this knowledge
              and remove it from any forms that use it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SharedKnowledge;